import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People,
  Storefront,
  Book,
  Receipt,
  Payment,
  Subscriptions,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Settings,
  ListAlt,
  WebhookOutlined,
  SystemUpdate,
  Apartment,
  Place,
  ContactMail,
  AccountBalanceWallet,
  EventNote,
  Category,
  Inventory2,
  Percent,
  Security,
  LocalOffer,
  FolderOpen,
  MailOutline,
} from '@mui/icons-material';
import { isSuperAdmin } from '@/utils/roles';
import { useAuth } from '../auth/AuthContext';

const SYSTEM_PATH_PREFIX = '/system/';

function isStructurePath(pathname: string): boolean {
  return (
    pathname.startsWith('/organization-units') ||
    pathname.startsWith('/organization-addresses') ||
    pathname.startsWith('/organization-contacts')
  );
}

function isMasterDataPath(pathname: string): boolean {
  return (
    pathname.startsWith('/payment-gateways') ||
    pathname.startsWith('/payment-terms') ||
    pathname.startsWith('/plans') ||
    pathname.startsWith('/products') ||
    pathname.startsWith('/tax-rules')
  );
}

export type SidebarProps = {
  /** Desktop mini drawer: icons only */
  collapsed?: boolean;
  /** Desktop: toggle collapse (rendered inside the drawer panel) */
  onToggleCollapse?: () => void;
  /** Close mobile drawer after navigation */
  onNavigate?: () => void;
};

export default function Sidebar({ collapsed = false, onToggleCollapse, onNavigate }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [systemOpen, setSystemOpen] = useState(false);
  const [structureOpen, setStructureOpen] = useState(false);
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [systemMenuAnchor, setSystemMenuAnchor] = useState<null | HTMLElement>(null);
  const [structureMenuAnchor, setStructureMenuAnchor] = useState<null | HTMLElement>(null);
  const [masterDataMenuAnchor, setMasterDataMenuAnchor] = useState<null | HTMLElement>(null);

  const isSystemRoute = location.pathname.startsWith(SYSTEM_PATH_PREFIX);
  const isStructureRoute = isStructurePath(location.pathname);
  const isMasterDataRoute = isMasterDataPath(location.pathname);

  useEffect(() => {
    if (isSystemRoute) setSystemOpen(true);
  }, [isSystemRoute]);

  useEffect(() => {
    if (isStructureRoute) setStructureOpen(true);
  }, [isStructureRoute]);

  useEffect(() => {
    if (isMasterDataRoute) setMasterDataOpen(true);
  }, [isMasterDataRoute]);

  const navItems = [
    { label: t('nav.dashboard'), path: '/dashboard', icon: <DashboardIcon /> },
    { label: t('nav.customers'), path: '/customers', icon: <People /> },
    { label: t('nav.invitations'), path: '/invitations', icon: <MailOutline /> },
    ...(isSuperAdmin(user)
      ? [{ label: t('nav.resellers'), path: '/resellers', icon: <Storefront /> }]
      : []),
    { label: t('nav.licences'), path: '/licences', icon: <Book /> },
    { label: t('nav.subscriptions'), path: '/subscriptions', icon: <Subscriptions /> },
    { label: t('nav.invoices'), path: '/invoices', icon: <Receipt /> },
    { label: t('nav.payments'), path: '/payments', icon: <Payment /> },
    ...(isSuperAdmin(user)
      ? [{ label: t('nav.securityDevices'), path: '/security-devices', icon: <Security /> }]
      : []),
    { label: t('nav.availableCoupons'), path: '/available-coupons', icon: <LocalOffer /> },
    { label: t('nav.resources'), path: '/resources', icon: <FolderOpen /> },
  ];

  const structureChildren = [
    { label: t('nav.orgUnits'), path: '/organization-units', icon: <Apartment fontSize="small" /> },
    {
      label: t('nav.orgAddresses'),
      path: '/organization-addresses',
      icon: <Place fontSize="small" />,
    },
    {
      label: t('nav.orgContacts'),
      path: '/organization-contacts',
      icon: <ContactMail fontSize="small" />,
    },
  ];

  const masterDataChildren = [
    {
      label: t('nav.paymentGateways'),
      path: '/payment-gateways',
      icon: <AccountBalanceWallet fontSize="small" />,
    },
    { label: t('nav.paymentTerms'), path: '/payment-terms', icon: <EventNote fontSize="small" /> },
    { label: t('nav.plans'), path: '/plans', icon: <Category fontSize="small" /> },
    { label: t('nav.products'), path: '/products', icon: <Inventory2 fontSize="small" /> },
    { label: t('nav.taxRules'), path: '/tax-rules', icon: <Percent fontSize="small" /> },
    { label: t('nav.coupons'), path: '/coupons', icon: <LocalOffer fontSize="small" /> },
  ];

  const systemChildren = [
    { label: t('nav.logs'), path: '/system/logs', icon: <ListAlt fontSize="small" /> },
    {
      label: t('nav.webhooks'),
      path: '/system/webhooks',
      icon: <WebhookOutlined fontSize="small" />,
    },
    {
      label: t('nav.appVersions'),
      path: '/system/versions',
      icon: <SystemUpdate fontSize="small" />,
    },
  ];

  const go = (path: string) => {
    navigate(path);
    onNavigate?.();
    setSystemMenuAnchor(null);
    setStructureMenuAnchor(null);
    setMasterDataMenuAnchor(null);
  };

  const collapseToggle = onToggleCollapse ? (
    <Tooltip
      title={t('nav.expandSidebar')}
      placement="right"
      enterDelay={300}
      disableHoverListener={!collapsed}
      disableFocusListener={!collapsed}
      disableTouchListener={!collapsed}
    >
      <ListItemButton
        onClick={onToggleCollapse}
        aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        sx={{
          minHeight: 48,
          justifyContent: collapsed ? 'center' : undefined,
          px: collapsed ? 1 : 2,
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0.5,
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 56,
            justifyContent: 'center',
            color: 'inherit',
          }}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </ListItemIcon>
        {!collapsed && <ListItemText primary={t('nav.collapseSidebar')} />}
      </ListItemButton>
    </Tooltip>
  ) : null;

  const renderMainNav = () =>
    navItems.map(({ label, path, icon }) => {
      const selected = location.pathname === path || location.pathname.startsWith(`${path}/`);
      const button = (
        <ListItemButton
          selected={selected}
          onClick={() => go(path)}
          sx={{
            minHeight: 48,
            justifyContent: collapsed ? 'center' : undefined,
            px: collapsed ? 1 : 2,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 56,
              justifyContent: 'center',
              color: 'inherit',
            }}
          >
            {icon}
          </ListItemIcon>
          {!collapsed && <ListItemText primary={label} />}
        </ListItemButton>
      );

      return (
        <Tooltip
          key={path}
          title={label}
          placement="right"
          enterDelay={300}
          disableHoverListener={!collapsed}
          disableFocusListener={!collapsed}
          disableTouchListener={!collapsed}
        >
          {button}
        </Tooltip>
      );
    });

  const renderNested = (children: { label: string; path: string; icon: React.ReactNode }[]) =>
    children.map(({ label, path, icon }) => {
      const selected = location.pathname === path || location.pathname.startsWith(`${path}/`);
      return (
        <ListItemButton
          key={path}
          selected={selected}
          onClick={() => go(path)}
          sx={{ pl: collapsed ? 1 : 4, minHeight: 44, py: 0.75 }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 40,
              justifyContent: 'center',
              color: 'inherit',
            }}
          >
            {icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
          )}
        </ListItemButton>
      );
    });

  const systemSectionCollapsed = isSuperAdmin(user) ? (
    <>
      <Tooltip title={t('nav.systemSettings')} placement="right" enterDelay={300}>
        <ListItemButton
          selected={isSystemRoute}
          onClick={(e) => setSystemMenuAnchor(e.currentTarget)}
          sx={{ justifyContent: 'center', px: 1, minHeight: 48 }}
          aria-haspopup="true"
          aria-expanded={Boolean(systemMenuAnchor)}
        >
          <Settings />
        </ListItemButton>
      </Tooltip>
      <Menu
        anchorEl={systemMenuAnchor}
        open={Boolean(systemMenuAnchor)}
        onClose={() => setSystemMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {systemChildren.map(({ label, path }) => (
          <MenuItem
            key={path}
            onClick={() => go(path)}
            selected={location.pathname === path || location.pathname.startsWith(`${path}/`)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : null;

  const systemSectionExpanded = isSuperAdmin(user) ? (
    <>
      <ListItemButton
        onClick={() => setSystemOpen((o) => !o)}
        selected={isSystemRoute}
        sx={{ minHeight: 48, px: collapsed ? 1 : 2 }}
      >
        <ListItemIcon
          sx={{ minWidth: collapsed ? 0 : 56, justifyContent: 'center', color: 'inherit' }}
        >
          <Settings />
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText primary={t('nav.systemSettings')} />
            {systemOpen ? <ExpandLess /> : <ExpandMore />}
          </>
        )}
      </ListItemButton>
      <Collapse in={systemOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {renderNested(systemChildren)}
        </List>
      </Collapse>
    </>
  ) : null;

  const structureSectionCollapsed = isSuperAdmin(user) ? (
    <>
      <Tooltip title={t('nav.customerStructure')} placement="right" enterDelay={300}>
        <ListItemButton
          selected={isStructureRoute}
          onClick={(e) => setStructureMenuAnchor(e.currentTarget)}
          sx={{ justifyContent: 'center', px: 1, minHeight: 48 }}
          aria-haspopup="true"
        >
          <Apartment />
        </ListItemButton>
      </Tooltip>
      <Menu
        anchorEl={structureMenuAnchor}
        open={Boolean(structureMenuAnchor)}
        onClose={() => setStructureMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {structureChildren.map(({ label, path }) => (
          <MenuItem
            key={path}
            onClick={() => go(path)}
            selected={location.pathname === path || location.pathname.startsWith(`${path}/`)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : null;

  const structureSectionExpanded = isSuperAdmin(user) ? (
    <>
      <ListItemButton
        onClick={() => setStructureOpen((o) => !o)}
        selected={isStructureRoute}
        sx={{ minHeight: 48, px: collapsed ? 1 : 2 }}
      >
        <ListItemIcon
          sx={{ minWidth: collapsed ? 0 : 56, justifyContent: 'center', color: 'inherit' }}
        >
          <Apartment />
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText primary={t('nav.customerStructure')} />
            {structureOpen ? <ExpandLess /> : <ExpandMore />}
          </>
        )}
      </ListItemButton>
      <Collapse in={structureOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {renderNested(structureChildren)}
        </List>
      </Collapse>
    </>
  ) : null;

  const masterDataSectionCollapsed = isSuperAdmin(user) ? (
    <>
      <Tooltip title={t('nav.masterData')} placement="right" enterDelay={300}>
        <ListItemButton
          selected={isMasterDataRoute}
          onClick={(e) => setMasterDataMenuAnchor(e.currentTarget)}
          sx={{ justifyContent: 'center', px: 1, minHeight: 48 }}
          aria-haspopup="true"
        >
          <Category />
        </ListItemButton>
      </Tooltip>
      <Menu
        anchorEl={masterDataMenuAnchor}
        open={Boolean(masterDataMenuAnchor)}
        onClose={() => setMasterDataMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {masterDataChildren.map(({ label, path }) => (
          <MenuItem
            key={path}
            onClick={() => go(path)}
            selected={location.pathname === path || location.pathname.startsWith(`${path}/`)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  ) : null;

  const masterDataSectionExpanded = isSuperAdmin(user) ? (
    <>
      <ListItemButton
        onClick={() => setMasterDataOpen((o) => !o)}
        selected={isMasterDataRoute}
        sx={{ minHeight: 48, px: collapsed ? 1 : 2 }}
      >
        <ListItemIcon
          sx={{ minWidth: collapsed ? 0 : 56, justifyContent: 'center', color: 'inherit' }}
        >
          <Category />
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText primary={t('nav.masterData')} />
            {masterDataOpen ? <ExpandLess /> : <ExpandMore />}
          </>
        )}
      </ListItemButton>
      <Collapse in={masterDataOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {renderNested(masterDataChildren)}
        </List>
      </Collapse>
    </>
  ) : null;

  return (
    <List sx={onNavigate ? { pt: '64px' } : { pt: 1 }}>
      {collapseToggle}
      {renderMainNav()}
      <Divider sx={{ my: 1 }} />
      {collapsed ? structureSectionCollapsed : structureSectionExpanded}
      {collapsed ? masterDataSectionCollapsed : masterDataSectionExpanded}
      <Divider sx={{ my: 1 }} />
      {collapsed ? systemSectionCollapsed : systemSectionExpanded}
    </List>
  );
}
